import { cloudinaryUrl } from '@realadvisor/cloudinary';

import {
  type EnhancedVersion,
  type ListingImageVersion,
  PROPERTY_IMAGE_VERSION_CONFLICT,
} from './enhance-image-types';

export type DisplayImageVersion = ListingImageVersion | EnhancedVersion;

const HEIC_EXTENSION = /\.(heic|heif)$/i;

export const getEnhanceSourceUrl = (
  url: string | null,
  maxDimension: number,
): string | null => {
  if (!url || !HEIC_EXTENSION.test(url.split(/[?#]/)[0])) {
    return url;
  }

  return cloudinaryUrl(
    { f: 'jpg', q: 90, w: maxDimension, h: maxDimension, c: 'limit' },
    url,
  );
};

export type ListingVersionAction = 'current' | 'restore' | 'save';

type CurrentImage = {
  id: string;
  url: string | null;
};

export const buildListingImageVersions = (
  currentImage: CurrentImage,
  savedVersions: ListingImageVersion[],
): ListingImageVersion[] => {
  const sortedVersions = [...savedVersions].sort(
    (left, right) =>
      left.createdAt.localeCompare(right.createdAt) ||
      left.id.localeCompare(right.id),
  );

  if (sortedVersions.some(version => version.imageId === currentImage.id)) {
    return sortedVersions;
  }

  return [
    ...sortedVersions,
    {
      kind: 'saved',
      id: `current-${currentImage.id}`,
      imageId: currentImage.id,
      imageUrl: currentImage.url,
      sourceImageId: null,
      prompt: null,
      createdAt: '',
    },
  ];
};

export const getListingVersionAction = (
  version: DisplayImageVersion,
  currentImageId: string,
): ListingVersionAction => {
  if (version.kind === 'draft') {
    return 'save';
  }

  return version.imageId === currentImageId ? 'current' : 'restore';
};

export const isPropertyImageVersionConflict = (error: unknown): boolean =>
  error instanceof Error &&
  error.message.includes(PROPERTY_IMAGE_VERSION_CONFLICT);
